import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore, type Role } from '@/stores/auth';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    requiresAuth?: boolean;
    requiresAnonymous?: boolean;
    allowedRoles?: Role[];
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { title: 'Start' },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { title: 'Anmelden', requiresAnonymous: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { title: 'Registrieren', requiresAnonymous: true },
  },
  {
    path: '/register/success',
    name: 'register-success',
    component: () => import('@/views/auth/RegisterSuccessView.vue'),
    meta: { title: 'Bestätigungs-Email gesendet' },
  },
  {
    path: '/activate/:token',
    name: 'activate',
    component: () => import('@/views/auth/ActivateView.vue'),
    meta: { title: 'Konto aktivieren' },
  },
  {
    path: '/password-reset',
    name: 'password-reset',
    component: () => import('@/views/auth/PasswordResetRequestView.vue'),
    meta: { title: 'Passwort zurücksetzen', requiresAnonymous: true },
  },
  {
    path: '/password-reset/confirm/:token',
    name: 'password-reset-confirm',
    component: () => import('@/views/auth/PasswordResetConfirmView.vue'),
    meta: { title: 'Neues Passwort setzen' },
  },
  {
    path: '/bewerbung',
    name: 'applicant-wizard',
    component: () => import('@/modules/applicants/views/WizardView.vue'),
    meta: {
      title: 'Mein Ansuchen',
      requiresAuth: true,
      allowedRoles: ['applicant'],
    },
  },
  {
    path: '/bewerbung/eingereicht',
    name: 'applicant-submitted',
    component: () => import('@/modules/applicants/views/WizardSubmittedView.vue'),
    meta: {
      title: 'Ansuchen eingereicht',
      requiresAuth: true,
      allowedRoles: ['applicant'],
    },
  },
  {
    path: '/admin/bewerber',
    name: 'admin-applicants',
    component: () => import('@/modules/applicants/views/AdminApplicantsListView.vue'),
    meta: {
      title: 'Bewerber',
      requiresAuth: true,
      allowedRoles: ['administrator'],
    },
  },
  {
    path: '/admin/bewerber/:id',
    name: 'admin-applicant-detail',
    component: () => import('@/modules/applicants/views/AdminApplicantDetailView.vue'),
    meta: {
      title: 'Bewerber-Details',
      requiresAuth: true,
      allowedRoles: ['administrator'],
    },
  },
  // ─── Admin-Verwaltung (Phase 11, Subagent A) ───
  {
    path: '/admin/curriculums',
    name: 'admin-curriculums',
    component: () => import('@/modules/curriculums/views/AdminCurriculumsListView.vue'),
    meta: { title: 'Lehrpläne', requiresAuth: true, allowedRoles: ['administrator'] },
  },
  {
    path: '/admin/curriculums/:id',
    name: 'admin-curriculum-detail',
    component: () => import('@/modules/curriculums/views/AdminCurriculumDetailView.vue'),
    meta: { title: 'Lehrplan-Details', requiresAuth: true, allowedRoles: ['administrator'] },
  },
  {
    path: '/admin/subjects',
    name: 'admin-subjects',
    component: () => import('@/modules/subjects/views/AdminSubjectsListView.vue'),
    meta: { title: 'Fächer', requiresAuth: true, allowedRoles: ['administrator'] },
  },
  {
    path: '/admin/brp-fachbereiche',
    name: 'admin-brp-fachbereiche',
    component: () => import('@/modules/brp-fachbereiche/views/AdminBrpFachbereicheView.vue'),
    meta: { title: 'BRP-Fachbereiche', requiresAuth: true, allowedRoles: ['administrator'] },
  },
  {
    path: '/admin/teachers',
    name: 'admin-teachers',
    component: () => import('@/modules/teachers/views/AdminTeachersListView.vue'),
    meta: { title: 'Lehrkräfte', requiresAuth: true, allowedRoles: ['administrator'] },
  },

  // ─── Admin-Ops (Phase 11, Subagent B) ───
  {
    path: '/admin/audit-log',
    name: 'admin-audit-log',
    component: () => import('@/modules/audit/views/AdminAuditLogView.vue'),
    meta: { title: 'Audit-Log', requiresAuth: true, allowedRoles: ['administrator'] },
  },
  {
    path: '/admin/config',
    name: 'admin-config',
    component: () => import('@/modules/config-admin/views/AdminConfigView.vue'),
    meta: { title: 'Konfiguration', requiresAuth: true, allowedRoles: ['administrator'] },
  },
  {
    path: '/admin/dsgvo',
    name: 'admin-gdpr',
    component: () => import('@/modules/gdpr-admin/views/AdminGdprRequestsView.vue'),
    meta: { title: 'DSGVO-Anfragen', requiresAuth: true, allowedRoles: ['administrator'] },
  },
  {
    path: '/admin/email-outbox',
    name: 'admin-email-outbox',
    component: () => import('@/modules/email-outbox/views/AdminEmailOutboxView.vue'),
    meta: { title: 'Email-Outbox', requiresAuth: true, allowedRoles: ['administrator'] },
  },
  {
    path: '/admin/document-templates',
    name: 'admin-document-templates',
    component: () => import('@/modules/document-templates/views/AdminDocumentTemplatesView.vue'),
    meta: { title: 'Word-Vorlagen', requiresAuth: true, allowedRoles: ['administrator'] },
  },

  // ─── Teacher-Portal (Phase 11, Subagent C) ───
  {
    path: '/lehrer/pruefungen',
    name: 'teacher-exams',
    component: () => import('@/modules/teacher-exams/views/TeacherExamsView.vue'),
    meta: { title: 'Meine Prüfungen', requiresAuth: true, allowedRoles: ['teacher'] },
  },
  {
    path: '/lehrer/noten-erfassen',
    name: 'teacher-grading',
    component: () => import('@/modules/teacher-grading/views/TeacherGradingView.vue'),
    meta: { title: 'Noten erfassen', requiresAuth: true, allowedRoles: ['teacher'] },
  },

  // ─── Student-Portal (Phase 11, Subagent D) ───
  {
    path: '/student/dashboard',
    name: 'student-dashboard',
    component: () => import('@/modules/student-portal/views/StudentDashboardView.vue'),
    meta: { title: 'Mein Studium', requiresAuth: true, allowedRoles: ['student'] },
  },
  {
    path: '/student/pruefungen',
    name: 'student-exams',
    component: () => import('@/modules/student-portal/views/StudentExamsView.vue'),
    meta: { title: 'Prüfungen', requiresAuth: true, allowedRoles: ['student'] },
  },
  {
    path: '/student/noten',
    name: 'student-grades',
    component: () => import('@/modules/student-results/views/StudentGradesView.vue'),
    meta: { title: 'Meine Noten', requiresAuth: true, allowedRoles: ['student'] },
  },
  {
    path: '/student/zertifikate',
    name: 'student-certificates',
    component: () => import('@/modules/student-results/views/StudentCertificatesView.vue'),
    meta: { title: 'Zertifikate', requiresAuth: true, allowedRoles: ['student'] },
  },

  // ─── Notifications (Phase 11, alle authenticated) ───
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/modules/notifications/views/NotificationsListView.vue'),
    meta: { title: 'Nachrichten', requiresAuth: true },
  },

  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/views/ForbiddenView.vue'),
    meta: { title: 'Kein Zugriff' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Nicht gefunden' },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to) => {
  const auth = useAuthStore();

  if (to.meta.requiresAnonymous && auth.isAuthenticated) {
    return { path: '/' };
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return {
      path: '/login',
      query: { redirect: to.fullPath },
    };
  }

  if (to.meta.allowedRoles && to.meta.allowedRoles.length > 0) {
    if (!auth.isAuthenticated) {
      return {
        path: '/login',
        query: { redirect: to.fullPath },
      };
    }
    const userRole = auth.role;
    if (!userRole || !to.meta.allowedRoles.includes(userRole)) {
      return { path: '/forbidden' };
    }
  }

  return true;
});

router.afterEach((to) => {
  const base = 'Externist-Portal';
  document.title = to.meta.title ? `${to.meta.title as string} · ${base}` : base;
});

export default router;
